import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Card,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControlLabel,
  Radio,
  IconButton,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import PropTypes from 'prop-types';
import { getScheduleReport } from '../../services/ruleset.service';
import useChart from '../../hooks/charts.hook';
import Datepicker from '../form-elements/datepicker.component';
import { formatDate, getDate } from '../../utils/datetime.util';
import SnackbarInfo from '../common/snackbar-info.component';
import Iconify from '../common/iconify.component';
import { CONTENT_SCHEDULE_TYPE } from '../../config/const.config';

const ContentScheduleReports = ({
  channelId,
  reloadCounter,
  refreshCounter,
}) => {
  const [filters, setFilters] = useState({
    startDate: getDate(-(2 * 7)),
    endDate: getDate(12 * 7),
    reportType: CONTENT_SCHEDULE_TYPE.GENERAL,
    reloadCounter: 0,
  });
  const [expandScheduleReports, setExpandScheduleReports] = useState(false);
  const [graphData, setGraphData] = useState({
    liveData: [],
    scheduleData: [],
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const chartOptions = useChart({
    xaxis: { type: 'date' },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y) => (typeof y !== 'undefined' ? y.toFixed(0) : y),
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => value.toFixed(0),
      },
    },
    chart: {
      toolbar: {
        show: false,
        tools: {
          zoom: false,
          zoomin: false,
          zoomout: false,
        },
      },
    },
  });

  useEffect(() => {
    if (channelId) {
      let params = `?type=${filters.reportType}`;
      if (filters.startDate) {
        params += `&startDate=${formatDate(filters.startDate, 'YYYY-MM-DD')}`;
      }
      if (filters.endDate) {
        params += `&endDate=${formatDate(filters.endDate, 'YYYY-MM-DD')}`;
      }
      getScheduleReport(channelId, params)
        .then((res) => {
          const totalVideosData = [];
          const moviesData = [];
          const episodesData = [];
          const scheduleData = [];
          const descheduleData = [];

          Object.keys(res.data.liveData).forEach((date) => {
            const data = res.data.liveData[date];

            totalVideosData.push({ x: date, y: data.totalVideosCount });
            moviesData.push({ x: date, y: data.moviesCount });
            episodesData.push({ x: date, y: data.episodesCount });
          });

          Object.keys(res.data.scheduleData).forEach((date) => {
            const data = res.data.scheduleData[date];

            scheduleData.push({ x: date, y: data.scheduleCount });
            descheduleData.push({ x: date, y: data.descheduleCount });
          });

          const transformedLiveData = [
            { name: 'Total Videos', data: totalVideosData },
            { name: 'Movies', data: moviesData },
            { name: 'Episodes', data: episodesData },
          ];
          const transformedScheduleData = [
            { name: 'Schedule', data: scheduleData },
            { name: 'Deschedule', data: descheduleData },
          ];

          setGraphData({
            liveData: transformedLiveData,
            scheduleData: transformedScheduleData,
          });
        })
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';

          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message,
          });
        });
    }
  }, [filters.reloadCounter, reloadCounter, refreshCounter]);

  return (
    <>
      <Accordion
        key="content-schedule-reports"
        expanded={expandScheduleReports}
        onChange={() => setExpandScheduleReports(!expandScheduleReports)}
        variant="outlined"
        sx={{
          backgroundColor: expandScheduleReports ? 'white' : 'inherit',
          borderRadius: 1,
          borderStyle: 'dashed',
          boxShadow: 'none',
        }}
      >
        <AccordionSummary
          expandIcon={<Iconify icon="ic:round-expand-more" width={25} />}
        >
          <Iconify
            icon="material-symbols:insert-chart-outline-rounded"
            width={25}
          />
          &nbsp;
          <Typography variant="subtitle1">Schedule Reports</Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box display="flex">
              <Datepicker
                id="start_date"
                name="start_date"
                label="From Date"
                defaultValue={filters.startDate}
                clearable
                onChange={(v) => {
                  setFilters({
                    ...filters,
                    startDate: v,
                    reloadCounter: filters.reloadCounter + 1,
                  });
                }}
                sx={{ p: 0 }}
              />
              <Box>&nbsp;&nbsp;&nbsp;</Box>
              <Datepicker
                id="end_date"
                name="end_date"
                label="To Date"
                defaultValue={filters.endDate}
                clearable
                onChange={(v) => {
                  setFilters({
                    ...filters,
                    endDate: v,
                    reloadCounter: filters.reloadCounter + 1,
                  });
                }}
              />

              <Box>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Box>
              <Box display="flex">
                <FormControlLabel
                  value={CONTENT_SCHEDULE_TYPE.GENERAL}
                  control={
                    <Radio
                      checked={
                        filters.reportType === CONTENT_SCHEDULE_TYPE.GENERAL
                      }
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          reportType: e.target.value,
                          reloadCounter: filters.reloadCounter + 1,
                        })
                      }
                    />
                  }
                  label={<Box fontWeight="bold">General</Box>}
                />

                <FormControlLabel
                  value={CONTENT_SCHEDULE_TYPE.HERO}
                  control={
                    <Radio
                      checked={
                        filters.reportType === CONTENT_SCHEDULE_TYPE.HERO
                      }
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          reportType: e.target.value,
                          reloadCounter: filters.reloadCounter + 1,
                        })
                      }
                    />
                  }
                  label={<Box fontWeight="bold">Hero Playlist</Box>}
                />
              </Box>
            </Box>

            <IconButton
              aria-label="refresh"
              onClick={() =>
                setFilters({
                  ...filters,
                  reloadCounter: filters.reloadCounter + 1,
                })
              }
            >
              <Iconify icon="ic:twotone-refresh" />
            </IconButton>
          </Box>

          <Box display="flex" justifyContent="space-between" gap={1.5}>
            <Card sx={{ width: '100%' }}>
              <CardHeader
                title="Live Videos"
                subheader="Date wise number of live videos"
              />
              <Box sx={{ p: 3, pb: 1 }} dir="ltr">
                <Stack>
                  <ReactApexChart
                    type="line"
                    series={graphData.liveData}
                    options={chartOptions}
                    height={364}
                  />
                </Stack>
              </Box>
            </Card>

            <Card sx={{ width: '100%' }}>
              <CardHeader
                title="Schedule/Deschedule Videos"
                subheader="Date wise number of schedule/deschedule videos"
              />
              <Box sx={{ p: 3, pb: 1 }} dir="ltr">
                <Stack>
                  <ReactApexChart
                    key={`stats-${reloadCounter}-${refreshCounter}-${filters.reloadCounter}`}
                    type="line"
                    series={graphData.scheduleData}
                    options={chartOptions}
                    height={364}
                  />
                </Stack>
              </Box>
            </Card>
          </Box>
        </AccordionDetails>
      </Accordion>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ContentScheduleReports.propTypes = {
  channelId: PropTypes.number.isRequired,
  reloadCounter: PropTypes.number.isRequired,
  refreshCounter: PropTypes.number.isRequired,
};

export default ContentScheduleReports;
